import type { ResidentDocumentGroup } from '@/features/resident/types'
import { reimbursementTypeDisplayLabel } from '@/features/resident/utils/residentHeaderStatus'
import {
  getCurrentDocumentRow,
  getUploadedRowsForDocType,
  isDocTypeOccupied,
  isIncompleteAttachmentAction,
} from './reimbursementDocumentBridge'
import type {
  ReimbursementDocumentTrigger,
  ReimbursementDocumentType,
  ReimbursementFormLogicInput,
  ReimbursementWorkflowProgress,
} from '../types/reimbursementFormTypes'

const FINAL_INFO_DOC_TYPES: ReimbursementDocumentType[] = ['final_ledger', 'original_ledger']

const CORE_SECTION_DOC_TYPES: ReimbursementDocumentType[] = ['signed_lease', 'current_ledger']

function hasFirstMonthUnpaid(values: ReimbursementFormLogicInput['values']): boolean {
  return Boolean(
    String(values.first_month_not_paid_month ?? '').trim() ||
      String(values.first_month_not_paid_year ?? '').trim(),
  )
}

export function hasMoveOutDate(values: ReimbursementFormLogicInput['values']): boolean {
  return Boolean(String(values.moveout_date ?? '').trim())
}

function securityDepositAmount(values: ReimbursementFormLogicInput['values']): string {
  const raw = String(values.unused_security_deposit ?? '').trim()
  if (!raw || raw === '$0.00' || raw === '0' || raw === '0.00') return ''
  return raw
}

export function getEnabledDocTypes(input: ReimbursementFormLogicInput): ReimbursementDocumentType[] {
  const { values, extended } = input
  const reimbursementType = reimbursementTypeDisplayLabel(values.reimbursement_type)

  const types: ReimbursementDocumentType[] = ['signed_lease', 'current_ledger', 'final_ledger']

  if (extended.lease_modifications_at_move_in === true) {
    types.push('original_lease', 'original_ledger')
  }

  if (reimbursementType === 'Skipped') {
    types.push('skip_affidavit')
    if (extended.cares_act_applicable === true) types.push('cares_act_notice')
  } else if (reimbursementType === 'Eviction') {
    if (extended.cares_act_applicable === true) types.push('cares_act_notice')
    if (extended.skipped_during_eviction === true) {
      types.push('skip_affidavit')
    } else if (extended.skipped_during_eviction === false) {
      types.push('eviction_lawsuit')
      if (extended.alternate_court_document_applicable === true) {
        types.push('alternate_court_document')
      }
      if (extended.bankruptcy_applicable === true) types.push('bankruptcy_document')
      if (extended.appeal_applicable === true) {
        types.push('court_registry')
        if (extended.resident_paid_into_court === true) types.push('bond_registry_receipt')
      }
      if (extended.stipulation_applicable === true) {
        types.push('stipulation_agreement')
        if (extended.resident_defaulted_on_repayment === true) types.push('default_stipulation')
      }
    }
  }

  return types
}

export function getCoreSectionDocTypes(
  input: ReimbursementFormLogicInput,
): ReimbursementDocumentType[] {
  const types = [...CORE_SECTION_DOC_TYPES]
  if (input.extended.lease_modifications_at_move_in === true) {
    types.push('original_lease')
  }
  return types
}

export function getUniqueSectionDocTypes(
  input: ReimbursementFormLogicInput,
): ReimbursementDocumentType[] {
  const core = getCoreSectionDocTypes(input)
  return getEnabledDocTypes(input).filter(
    (t) => !core.includes(t) && !FINAL_INFO_DOC_TYPES.includes(t),
  )
}

export function isCoreFieldsComplete(input: ReimbursementFormLogicInput): boolean {
  const { values, extended } = input
  const reimbursementType = reimbursementTypeDisplayLabel(values.reimbursement_type)

  return !!(
    hasFirstMonthUnpaid(values) &&
    values.rental_status_date &&
    reimbursementType &&
    extended.security_deposit_collected !== null &&
    (extended.security_deposit_collected !== true || !!securityDepositAmount(values)) &&
    extended.lease_modifications_at_move_in !== null
  )
}

export function isUniqueFieldsComplete(input: ReimbursementFormLogicInput): boolean {
  const { values, extended } = input
  const reimbursementType = reimbursementTypeDisplayLabel(values.reimbursement_type)

  return !!(
    values.reason_for_default &&
    (reimbursementType === 'Skipped'
      ? extended.cares_act_applicable !== null
      : reimbursementType === 'Eviction'
        ? extended.skipped_during_eviction !== null &&
          extended.cares_act_applicable !== null &&
          (extended.skipped_during_eviction === true ||
            (extended.using_third_party_eviction_services !== null &&
              extended.utilizing_legal_team_for_eviction !== null &&
              extended.bankruptcy_applicable !== null &&
              extended.appeal_applicable !== null &&
              extended.stipulation_applicable !== null &&
              extended.alternate_court_document_applicable !== null &&
              (extended.using_third_party_eviction_services !== true ||
                !!extended.third_party_servicer_name.trim()) &&
              (extended.appeal_applicable !== true || extended.resident_paid_into_court !== null) &&
              (extended.stipulation_applicable !== true ||
                extended.resident_defaulted_on_repayment !== null)))
        : true)
  )
}

function isDocTypeLatestReviewIncomplete(
  input: ReimbursementFormLogicInput,
  docType: ReimbursementDocumentType,
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  const local = input.documents[docType]
  if (local?.status === 'incomplete' || local?.status === 'rejected') {
    return true
  }
  if (local) return false
  const rows = getUploadedRowsForDocType(residentDocuments, docType)
  const current = getCurrentDocumentRow(rows)
  if (!current) return false
  return isIncompleteAttachmentAction(current.action)
}

/** Required doc types present and latest attachment not marked incomplete/rejected. */
export function sectionDocsAcceptableForSubmit(
  input: ReimbursementFormLogicInput,
  types: ReimbursementDocumentType[],
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  return types.every((docType) => {
    if (!isDocTypeOccupied(docType, input.documents, residentDocuments)) {
      return false
    }
    return !isDocTypeLatestReviewIncomplete(input, docType, residentDocuments)
  })
}

export function isFinalSectionFieldsComplete(input: ReimbursementFormLogicInput): boolean {
  const { values, extended } = input
  return extended.payment_info_confirmed === true && hasMoveOutDate(values)
}

export function getFinalSectionDocTypes(input: ReimbursementFormLogicInput): ReimbursementDocumentType[] {
  const docTypes: ReimbursementDocumentType[] = ['final_ledger']
  if (input.extended.lease_modifications_at_move_in === true) {
    docTypes.push('original_ledger')
  }
  return docTypes
}

/** Required fields + uploads present (underwriter approval not required). */
export function isCoreSectionSubmitReady(
  input: ReimbursementFormLogicInput,
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  return (
    isCoreFieldsComplete(input) &&
    sectionDocsAcceptableForSubmit(input, getCoreSectionDocTypes(input), residentDocuments)
  )
}

export function isUniqueSectionSubmitReady(
  input: ReimbursementFormLogicInput,
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  return (
    isUniqueFieldsComplete(input) &&
    sectionDocsAcceptableForSubmit(input, getUniqueSectionDocTypes(input), residentDocuments)
  )
}

export function isFinalSectionSubmitReady(
  input: ReimbursementFormLogicInput,
  residentDocuments?: ResidentDocumentGroup | null,
): boolean {
  return (
    isFinalSectionFieldsComplete(input) &&
    sectionDocsAcceptableForSubmit(input, getFinalSectionDocTypes(input), residentDocuments)
  )
}

export function getDocumentTrigger(input: ReimbursementFormLogicInput): ReimbursementDocumentTrigger {
  const enabledDocs = getEnabledDocTypes(input).map((t) => input.documents[t])
  if (enabledDocs.every((d) => d?.status === 'approved')) return 'docs_approved'
  if (enabledDocs.some((d) => d?.status === 'uploaded' || d?.status === 'pending')) return 'uploaded'
  return 'awaiting'
}

export function getWorkflowProgress(input: ReimbursementFormLogicInput): ReimbursementWorkflowProgress {
  const { extended, documents } = input

  const allEnabledTypes = getEnabledDocTypes(input)

  if (
    allEnabledTypes.some(
      (t) => documents[t]?.status === 'incomplete' || documents[t]?.status === 'rejected',
    )
  ) {
    return 'action_needed'
  }

  const nonFinalDocTypes = allEnabledTypes.filter((t) => !FINAL_INFO_DOC_TYPES.includes(t))

  const coreFieldsComplete = isCoreFieldsComplete(input)
  const uniqueFieldsComplete = isUniqueFieldsComplete(input)

  const nonFinalDocsUploaded = nonFinalDocTypes.every((t) => documents[t] !== null)
  const nonFinalComplete = coreFieldsComplete && uniqueFieldsComplete && nonFinalDocsUploaded

  const finalInfoComplete = !!(
    extended.payment_info_confirmed &&
    documents.final_ledger?.status === 'approved' &&
    (extended.lease_modifications_at_move_in !== true ||
      documents.original_ledger?.status === 'approved')
  )

  if (nonFinalComplete && finalInfoComplete) return 'ready_for_review'
  if (nonFinalComplete) return 'awaiting_final_info'
  return 'info_needed'
}

export function getRejectedDocuments(input: ReimbursementFormLogicInput) {
  return getEnabledDocTypes(input)
    .filter(
      (t) =>
        input.documents[t]?.status === 'incomplete' ||
        input.documents[t]?.status === 'rejected',
    )
    .map((t) => ({
      docType: t,
      label: t,
      reason: input.documents[t]?.rejectionReason,
    }))
}

/** Clear full-path eviction fields when user selects vacated-before-lawsuit Yes. */
export function getEvictionShortPathResetFields(): Partial<ReimbursementFormLogicInput['extended']> {
  return {
    alternate_court_document_applicable: null,
    using_third_party_eviction_services: null,
    utilizing_legal_team_for_eviction: null,
    third_party_servicer_name: '',
    bankruptcy_applicable: null,
    appeal_applicable: null,
    stipulation_applicable: null,
    resident_paid_into_court: null,
    resident_defaulted_on_repayment: null,
  }
}

export const EVICTION_SHORT_PATH_DISABLED_DOC_TYPES: ReimbursementDocumentType[] = [
  'eviction_lawsuit',
  'alternate_court_document',
  'bankruptcy_document',
  'court_registry',
  'bond_registry_receipt',
  'stipulation_agreement',
  'default_stipulation',
]
